import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Fade from '@material-ui/core/Fade'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import Badge from '@material-ui/core/Badge'
import Popover from '@material-ui/core/Popover'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FilterListIcon from '@material-ui/icons/FilterList'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useAuth } from '../../../Contexts/AuthProvider'
import useTranslation from '../../../Hooks/useTranslation'
import SearchInput from '../../../Components/SearchInput'
import TablePagination from '../../PredictiveMaintenance/Equipment/TablePagination'
import DmsService from '../../../Services/DMS/dms-api.service'
import AlertToastr from '../../../utils/alert'
import DeleteItemModal from '../../../Components/modals/delete-item-modal/delete-item-modal.component'

const RemoveIcon = () => <div className="removeIcon" />

const ascConst = 'ascending'
const descConst = 'descending'
const arrowDownClassName = 'hasArrow'
const arrowUpClassName = 'hasArrow up'

const SORT_KEY_TO_API = {
    timestamp: 'Timestamp',
    uId: 'unitUId',
    name: 'unitName',
    action: 'ActionType',
    actionBy: 'ActionBy',
}


const parseLogTimestampToDate = (raw) => {
    const s = String(raw).trim()
    if (!s) return new Date(NaN)
    const hasExplicitZone =
        /Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)
    if (hasExplicitZone) {
        return new Date(s)
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
        return new Date(`${s}Z`)
    }
    return new Date(s)
}

const formatLogTimestamp = (iso) => {
    if (!iso) return ''
    const d = parseLogTimestampToDate(iso)
    if (Number.isNaN(d.getTime())) return String(iso)
    return d.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    })
}

/** ISO-8601 string for delete API (`logs[].timestamp`). */
const timestampToIsoForApi = (raw) => {
    if (raw == null || raw === '') return ''
    const s = String(raw).trim()
    const d = parseLogTimestampToDate(s)
    if (Number.isNaN(d.getTime())) return s
    return d.toISOString()
}

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

const rowKey = (row, index) => {
    const u = row.uid ?? row.uId
    const t = row.timestamp ?? ''
    if (u != null && String(u) !== '') {
        return `${u}-${t}-${index}`
    }
    return `log-${index}`
}

const normalizeUsersShortList = (payload) => {
    if (payload == null) {
        return []
    }
    if (Array.isArray(payload)) {
        return payload
    }
    if (Array.isArray(payload.data)) {
        return payload.data
    }
    if (Array.isArray(payload.items)) {
        return payload.items
    }
    if (Array.isArray(payload.users)) {
        return payload.users
    }
    return []
}

/** Resolves user rows from GET /users/short — body may be the array or Axios `{ data: [...] }`. */
const SYSTEM_USER_GUID = '00000000-0000-0000-0000-000000000000'

/** Matches `User.isSuperuser()` in `Models/User.js` */
const isSuperuserRole = (role) => {
    if (role == null || role === '') return false
    const r = String(role).toLowerCase()
    return r === 'superuser' || r === 'master'
}

const isSystemUserRole = (role) => {
    if (role == null || role === '') return false
    return String(role).toLowerCase() === 'system'
}

/** When short user name is literally "Superuser", role may still omit superuser/master */
const isSuperuserDisplayName = (name) => {
    if (name == null || name === '') return false
    return String(name).trim().toLowerCase() === 'superuser'
}

const extractUsersShortList = (queryResult) => {
    if (queryResult == null) {
        return []
    }
    if (Array.isArray(queryResult)) {
        return queryResult
    }
    if (Array.isArray(queryResult.data)) {
        return queryResult.data
    }
    return normalizeUsersShortList(queryResult)
}

const UnitSettingsLog = () => {
    const translation = useTranslation()
    const classes = useStyles()
    const queryClient = useQueryClient()
    const { userLoaded, currentUser } = useAuth()
    const currentCompany = useSelector(state => state.dashboard.currentCompany)

    const [searchFilter, setSearchFilter] = useState()
    const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: descConst })
    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(15)
    const [columnFilters, setColumnFilters] = useState({
        unitUIds: [],
        unitNames: [],
        actionBys: [],
    })

    const filtersKey = useMemo(
        () =>
            `${columnFilters.unitUIds.join('\u0001')}|${columnFilters.unitNames.join('\u0001')}|${columnFilters.actionBys.join('\u0001')}`,
        [columnFilters],
    )

    const [filterAnchor, setFilterAnchor] = useState({ key: null, el: null })
    const [pendingFilterValue, setPendingFilterValue] = useState([])
    const filterAnchorElRef = useRef(null)
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
    const columnFiltersRef = useRef(columnFilters)
    columnFiltersRef.current = columnFilters

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [logPendingDelete, setLogPendingDelete] = useState(null)

    const isSuperUser = useMemo(() => currentUser?.isSuperuser?.() ?? false, [currentUser])

    const companyConfig = useMemo(() => {
        if (!isSuperUser) {
            return {}
        }
        return {
            headers: {
                CompanyId: currentCompany?.id,
            },
        }
    }, [isSuperUser, currentCompany?.id])

    const queryEnabled =
        userLoaded &&
        currentUser &&
        (!isSuperUser || Boolean(currentCompany?.id))

    const sortByApi = SORT_KEY_TO_API[sortConfig.key] || 'Timestamp'
    const sortDescending = sortConfig.direction === descConst

    const logsQuery = useQuery(
        [
            'unitSettingsLogs',
            currentCompany?.id,
            pageNumber,
            pageSize,
            sortByApi,
            sortDescending,
            searchFilter,
            columnFilters.unitUIds,
            columnFilters.unitNames,
            columnFilters.actionBys,
        ],
        () =>
            DmsService.getUnitSettingsLogs(
                {
                    pageNumber,
                    pageSize,
                    sortBy: sortByApi,
                    sortDescending,
                    searchTerm: searchFilter,
                    unitUIds: columnFilters.unitUIds,
                    unitNames: columnFilters.unitNames,
                    actionBys: columnFilters.actionBys,
                },
                companyConfig,
            ),
        {
            enabled: queryEnabled,
            keepPreviousData: true,
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const removeLogMutation = useMutation(({ body, config }) =>
        DmsService.removeUnitSettingsLogsEntries(body, config),
    )

    const closeDeleteLogModal = useCallback(() => {
        setLogPendingDelete(null)
        setDeleteModalOpen(false)
    }, [])

    const confirmDeleteLog = useCallback(async () => {
        if (!logPendingDelete) return
        const unitUId = logPendingDelete.unitUId != null ? String(logPendingDelete.unitUId) : ''
        const timestamp = timestampToIsoForApi(logPendingDelete.timestamp)
        try {
            const response = await removeLogMutation.mutateAsync({
                body: { logs: [{ unitUId, timestamp }] },
                config: companyConfig,
            })
            const apiMsg = response?.data?.message ?? response?.data?.Message
            AlertToastr.showAlert(
                typeof apiMsg === 'string' && apiMsg.trim() !== ''
                    ? apiMsg
                    : translation.dataUsageUnitSettingsLogDeleteSuccess,
            )
            await queryClient.invalidateQueries('unitSettingsLogs')
        } catch (error) {
            const msg =
                error?.response?.data?.Message ||
                error?.response?.data?.message ||
                error?.message
            if (msg) {
                AlertToastr.showErrorAlert(msg)
            }
        } finally {
            closeDeleteLogModal()
        }
    }, [
        logPendingDelete,
        removeLogMutation,
        companyConfig,
        queryClient,
        translation.dataUsageUnitSettingsLogDeleteSuccess,
        closeDeleteLogModal,
    ])

    const usersShortQuery = useQuery(
        ['usersShort', currentCompany?.id],
        () => DmsService.getUsersShort(companyConfig),
        {
            enabled: queryEnabled,
            staleTime: 60 * 1000,
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const actionByIdToUserMeta = useMemo(() => {
        const list = extractUsersShortList(usersShortQuery.data)
        const map = Object.create(null)
        list.forEach((u) => {
            const id = u.id ?? u.userId
            const name = (u.name ?? u.userName ?? u.fullName)?.trim()
            if (id == null || !name) {
                return
            }
            const email = (u.email ?? u.userEmail ?? '').trim()
            map[String(id).toLowerCase()] = {
                name,
                email,
                role: u.role,
            }
        })
        return map
    }, [usersShortQuery.data])

    const getActionByDisplay = useCallback(
        (actionById) => {
            if (actionById == null || actionById === '') {
                return ''
            }
            const raw = String(actionById).trim()
            const lookupKey = raw.toLowerCase()
            if (lookupKey === SYSTEM_USER_GUID) {
                return translation.dataUsageUnitSettingsLogActionBySystemUser
            }
            const meta = actionByIdToUserMeta[lookupKey]
            const label = meta?.name ?? raw

            const showEmail =
                meta &&
                meta.email &&
                !isSuperuserRole(meta.role) &&
                !isSystemUserRole(meta.role) &&
                !isSuperuserDisplayName(meta.name) &&
                !isSuperuserDisplayName(label)

            if (showEmail) {
                return `${label} / ${meta.email}`
            }
            return label
        },
        [actionByIdToUserMeta, translation],
    )

    const items = useMemo(() => {
        const raw = logsQuery.data?.data?.items
        return Array.isArray(raw) ? raw : []
    }, [logsQuery.data])
    const totalCount = logsQuery.data?.data?.totalCount ?? 0
    const serverFilterOptions = logsQuery.data?.data?.filters ?? null

    const columnFilterOptions = useMemo(() => {
        const f = serverFilterOptions && typeof serverFilterOptions === 'object' ? serverFilterOptions : {}
        const pick = (k, ...altKeys) => {
            for (const key of [k, ...altKeys]) {
                if (Array.isArray(f[key])) {
                    return [...f[key]]
                }
            }
            return []
        }
        return {
            unitUIds: pick('unitUIds', 'unitUids'),
            unitNames: pick('unitNames'),
            actionBys: pick('actionBys'),
        }
    }, [serverFilterOptions])

    const setColumnFilter = useCallback((key, value) => {
        setColumnFilters((prev) => ({ ...prev, [key]: value }))
    }, [])

    const handleFilterIconClick = useCallback((key, event) => {
        setFilterAnchor((prev) =>
            prev.key === key ? { key: null, el: null } : { key, el: event.currentTarget.closest('th') || event.currentTarget },
        )
    }, [])

    const handleCloseFilterPopper = useCallback(() => {
        setFilterAnchor({ key: null, el: null })
        setPopoverAnchorEl(null)
    }, [])

    useEffect(() => {
        if (filterAnchor.key != null) {
            setPendingFilterValue(columnFiltersRef.current[filterAnchor.key] || [])
        }
    }, [filterAnchor.key])

    useLayoutEffect(() => {
        if (filterAnchor.key) {
            setPopoverAnchorEl(filterAnchorElRef.current)
        } else {
            setPopoverAnchorEl(null)
        }
    }, [filterAnchor.key, items])

    const pageCount = useMemo(() => {
        if (totalCount <= 0) {
            return 0
        }
        return Math.max(1, Math.ceil(totalCount / pageSize))
    }, [totalCount, pageSize])

    const pagination = useMemo(
        () => ({
            pageNumber,
            pageSize,
            itemsCount: items.length,
            itemsTotalCount: totalCount,
            pageCount: totalCount === 0 ? 0 : pageCount,
        }),
        [pageNumber, pageSize, items.length, totalCount, pageCount],
    )

    useEffect(() => {
        setPageNumber(1)
    }, [searchFilter, filtersKey])

    useEffect(() => {
        if (pageCount > 0 && pageNumber > pageCount) {
            setPageNumber(pageCount)
        }
    }, [pageCount, pageNumber])

    const requestSort = (key) => {
        let direction = ascConst
        if (sortConfig && sortConfig.key === key && sortConfig.direction === ascConst) {
            direction = descConst
        }
        setSortConfig({ key, direction })
        setPageNumber(1)
    }

    const getClassNamesFor = (name) => {
        if (!sortConfig || sortConfig.key !== name) {
            return
        }
        if (sortConfig.direction) {
            return sortConfig.direction === ascConst ? arrowDownClassName : arrowUpClassName
        }
    }

    const onPrevPage = useCallback(() => {
        setPageNumber(p => Math.max(1, p - 1))
        window.scrollTo(0, 0)
    }, [])

    const onNextPage = useCallback(() => {
        setPageNumber(p => Math.min(pageCount || 1, p + 1))
        window.scrollTo(0, 0)
    }, [pageCount])

    const handleRowCount = useCallback((count) => {
        setPageSize(count)
        setPageNumber(1)
    }, [])

    const isLoading = logsQuery.isLoading || logsQuery.isFetching

    return (
        <div className="pageCard">
            <div className="pageBtnSection pt0 mt5">
                <div className="searchHolder">
                    <div className="inputHolder" style={{ display: 'flex', verticalAlign: 'center' }}>
                        <SearchInput search={searchFilter} onChange={setSearchFilter} />
                    </div>
                </div>
            </div>
            <div className="tableHolder" style={{ position: 'relative', minHeight: 120 }}>
                <table>
                    <thead>
                        <tr className="headingRow">
                            <th
                                onClick={() => requestSort('timestamp')}
                                className={getClassNamesFor('timestamp')}
                            >
                                {translation.dataUsageUnitSettingsLogTimestampHeader}
                            </th>
                            <th
                                ref={filterAnchor.key === 'unitNames' ? (el) => { filterAnchorElRef.current = el } : undefined}
                                onClick={() => requestSort('name')}
                                className={getClassNamesFor('name')}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {translation.dataUsageUnitSettingsLogNameHeader}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleFilterIconClick('unitNames', e)
                                        }}
                                        style={{ padding: 4 }}
                                        aria-label={`Filter ${translation.dataUsageUnitSettingsLogNameHeader}`}
                                    >
                                        <Badge
                                            badgeContent={columnFilters.unitNames.length > 0 ? columnFilters.unitNames.length : null}
                                            color="primary"
                                            overlap="rectangular"
                                        >
                                            <FilterListIcon fontSize="medium" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                        </Badge>
                                    </IconButton>
                                </span>
                            </th>
                            <th
                                ref={filterAnchor.key === 'unitUIds' ? (el) => { filterAnchorElRef.current = el } : undefined}
                                onClick={() => requestSort('uId')}
                                className={getClassNamesFor('uId')}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {translation.dataUsageUnitSettingsLogUIdHeader}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleFilterIconClick('unitUIds', e)
                                        }}
                                        style={{ padding: 4 }}
                                        aria-label={`Filter ${translation.dataUsageUnitSettingsLogUIdHeader}`}
                                    >
                                        <Badge
                                            badgeContent={columnFilters.unitUIds.length > 0 ? columnFilters.unitUIds.length : null}
                                            color="primary"
                                            overlap="rectangular"
                                        >
                                            <FilterListIcon fontSize="medium" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                        </Badge>
                                    </IconButton>
                                </span>
                            </th>
                            <th
                            >
                                {translation.dataUsageUnitSettingsLogActionHeader}
                            </th>
                            <th
                                ref={filterAnchor.key === 'actionBys' ? (el) => { filterAnchorElRef.current = el } : undefined}
                                style={{ cursor: 'default' }}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {translation.dataUsageUnitSettingsLogActionByHeader}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleFilterIconClick('actionBys', e)
                                        }}
                                        style={{ padding: 4 }}
                                        aria-label={`Filter ${translation.dataUsageUnitSettingsLogActionByHeader}`}
                                    >
                                        <Badge
                                            badgeContent={columnFilters.actionBys.length > 0 ? columnFilters.actionBys.length : null}
                                            color="primary"
                                            overlap="rectangular"
                                        >
                                            <FilterListIcon fontSize="medium" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                        </Badge>
                                    </IconButton>
                                </span>
                            </th>
                            <th className="text-center" style={{ cursor: 'default' }}>
                                {translation.dataUsageUnitSettingsLogExcludeFromBillingHeader}
                            </th>
                            <th className="actionTableCell" aria-hidden />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, index) => {
                            const excludedFromBilling = Boolean(
                                row.excludeFromBilling ?? row.ExcludeFromBilling,
                            )
                            return (
                                <tr key={rowKey(row, index)}>
                                    <td>{formatLogTimestamp(row.timestamp)}</td>
                                    <td>{row.unitName ?? ''}</td>
                                    <td>{row.unitUId ?? ''}</td>
                                    <td>{row.actionType != null ? String(row.actionType) : ''}</td>
                                    <td>{getActionByDisplay(row.actionBy)}</td>
                                    <td className="text-center unitSettingsLogExcludeBillingCell">
                                        <span
                                            className="unitSettingsLogExcludeBillingReadonly"
                                            aria-label={
                                                excludedFromBilling
                                                    ? `${translation.dataUsageUnitSettingsLogExcludeFromBillingHeader}: yes`
                                                    : `${translation.dataUsageUnitSettingsLogExcludeFromBillingHeader}: no`
                                            }
                                        >
                                            {excludedFromBilling ? '\u2714' : '-'}
                                        </span>
                                    </td>
                                    <td className="actionTableCell">
                                        <IconButton
                                            size="small"
                                            className="deleteTableItemBtn"
                                            onClick={() => {
                                                setLogPendingDelete(row)
                                                setDeleteModalOpen(true)
                                            }}
                                            aria-label={translation.deleteButtonLabel}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <Popover
                    open={!!filterAnchor.key && !!popoverAnchorEl}
                    anchorEl={popoverAnchorEl}
                    onClose={handleCloseFilterPopper}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ style: { marginTop: 4, padding: 8, minWidth: 200, maxWidth: 320 } }}
                >
                    {filterAnchor.key && (() => {
                        const key = filterAnchor.key
                        const options = columnFilterOptions[key] || []
                        const toggleOption = (opt) => {
                            const next = pendingFilterValue.includes(opt)
                                ? pendingFilterValue.filter((o) => o !== opt)
                                : [...pendingFilterValue, opt]
                            setPendingFilterValue(next)
                        }
                        const handleApply = () => {
                            setColumnFilter(key, pendingFilterValue)
                            handleCloseFilterPopper()
                        }
                        const handleClear = () => {
                            setPendingFilterValue([])
                            setColumnFilter(key, [])
                            handleCloseFilterPopper()
                        }
                        return (
                            <div style={{ minWidth: 200 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        onClick={handleClear}
                                        disabled={pendingFilterValue.length === 0}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Clear filter
                                    </Button>
                                    <Button
                                        size="small"
                                        color="primary"
                                        onClick={handleApply}
                                        disabled={pendingFilterValue.length === 0}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Apply
                                    </Button>
                                </div>
                                <div
                                    className="dataUsageDetailedFilterListbox"
                                    style={{ maxHeight: 280, overflowY: 'auto', paddingLeft: 0 }}
                                >
                                    {options.map((opt) => (
                                        <div
                                            key={String(opt)}
                                            onClick={() => toggleOption(opt)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '6px 0',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    toggleOption(opt)
                                                }
                                            }}
                                        >
                                            <Checkbox
                                                color="primary"
                                                size="small"
                                                style={{ marginRight: 8, padding: 4 }}
                                                checked={pendingFilterValue.includes(opt)}
                                                onClick={(e) => { e.stopPropagation(); toggleOption(opt); }}
                                            />
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {key === 'actionBys' ? getActionByDisplay(opt) : String(opt)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })()}
                </Popover>
                <div className={classes.root}>
                    <Fade in={isLoading} unmountOnExit>
                        <CircularProgress />
                    </Fade>
                </div>
            </div>
            {pagination.itemsTotalCount > 0 && (
                <TablePagination
                    pagination={pagination}
                    onPrevPage={onPrevPage}
                    onNextPage={onNextPage}
                    handleRowCount={handleRowCount}
                />
            )}
            <DeleteItemModal
                isOpen={deleteModalOpen}
                isOpenManager={setDeleteModalOpen}
                loading={removeLogMutation.isLoading}
                onCancel={closeDeleteLogModal}
                message={translation.dataUsageUnitSettingsLogDeleteConfirmMessage}
                onDeleteModalClose={confirmDeleteLog}
            />
        </div>
    )
}

export default UnitSettingsLog
